import { createStore, applyMiddleware, compose } from "redux";
import createSagaMiddleware from "redux-saga";
import { persistStore, persistReducer, createMigrate } from "redux-persist";
import autoMergeLevel1 from "redux-persist/lib/stateReconciler/autoMergeLevel1";
import storage from "redux-persist/lib/storage";
import rootReducer, { RootState } from "../reducers";
import rootSaga from "../sagas";

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof compose;
  }
}

// Migration to handle old state structure
const migrations = {
  0: (state: any) => {
    // Remove old "user" key if it exists
    if (state && typeof state === 'object' && state.user) {
      const { user, ...rest } = state;
      console.log("🟡 Migration 0: Removed old 'user' key from persisted state");
      return rest;
    }
    return state;
  },
  1: (state: any) => {
    // Ensure only whitelisted keys are present
    const allowedKeys = [
      "login",
      "register",
      "authDetails",
      "loginAnotherWay",
      "forgotPassword",
      "resetPassword",
      "resetPasswordRequest",
      "profileInformation",
      "userSetup",
      "accountSetup",
    ];
    
    if (!state || typeof state !== 'object') {
      return state;
    }
    
    const filteredState: any = {};
    let hasFiltered = false;
    
    allowedKeys.forEach((key) => {
      if (state[key] !== undefined) {
        filteredState[key] = state[key];
      }
    });
    
    // Check if we filtered out any keys
    Object.keys(state).forEach((key) => {
      if (!allowedKeys.includes(key)) {
        hasFiltered = true;
        console.log(`🟡 Migration 1: Filtered out unknown key '${key}' from persisted state`);
      }
    });
    
    if (hasFiltered) {
      console.log("🟢 Migration 1: State cleaned, only allowed keys retained", Object.keys(filteredState));
    }
    
    return filteredState;
  },
};

const persistConfig = {
  key: "userdb", // changed from "root" to "userdb" for app-specific persist key
  version: 1, // Increment version to trigger migration
  migrate: createMigrate(migrations, { debug: false }),
  storage: {
    ...storage,
    setItem: (key: string, value: string) => storage.setItem(key, value),
    getItem: async (key: string) => {
      const encryptedValue = await storage.getItem(key);
      return encryptedValue;
    },
  },
  whitelist: ["authDetails", "profileInformation"], // Persist both auth and user profile details
  stateReconciler: autoMergeLevel1, // Use autoMergeLevel1 which automatically filters unknown keys
};

const sagaMiddleware = createSagaMiddleware();
const middleware = applyMiddleware(sagaMiddleware);

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer);

export const store = createStore(
  persistedReducer,
  composeEnhancers(middleware),
);

export const persistor = persistStore(store);

if (import.meta.env.MODE !== "test") {
  sagaMiddleware.run(rootSaga);
}

export default store;
